/*
 * RIDECLOUD THANK YOU PAGE
 * Style: Original RideCloud — white background, purple headings, orange accents
 * Shown after Zoho form submission (returnURL = /thank-you)
 *
 * SUBMISSION SUMMARY:
 * Reads 'ridecloud_lead_summary' from sessionStorage (saved by both
 * VehicleCatalogue InterestModal and ContactSection before form POST).
 * Displays a personalised confirmation card with the user's submitted data.
 * sessionStorage is cleared after reading to prevent stale data on revisit.
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { CheckCircle, ArrowLeft, Phone, Mail, Clock, User, Car, MessageSquare, Calendar } from "lucide-react";

const LOGO_ORANGE =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663326861339/FdaTdcVYATQM4PxPcH7X9e/ridecloud-master-logo-primary-orange_cb013153.png";

interface LeadSummary {
  name: string;
  phone: string | null;
  email: string | null;
  vehicle: string | null;
  comments: string | null;
  source: "vehicle-enquiry" | "contact-form";
  submittedAt: string;
}

const steps = [
  {
    number: "01",
    title: "Consultant Assigned",
    desc: "A dedicated RideCloud consultant will review your enquiry and be assigned to your account within the hour.",
  },
  {
    number: "02",
    title: "We'll Be In Touch",
    desc: "Expect a call or email within 24 business hours to discuss your vehicle selection, subscription term, and monthly budget.",
  },
  {
    number: "03",
    title: "Application & Approval",
    desc: "We'll guide you through a quick credit and affordability check — most approvals are completed within 48 hours.",
  },
  {
    number: "04",
    title: "Keys in Hand",
    desc: "Once approved, your vehicle is prepared, insured, and delivered to your door. Drive away with zero hassle.",
  },
];

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString("en-ZA", { dateStyle: "long", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function ThankYou() {
  const [lead, setLead] = useState<LeadSummary | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      const raw = sessionStorage.getItem("ridecloud_lead_summary");
      if (raw) {
        setLead(JSON.parse(raw));
        sessionStorage.removeItem("ridecloud_lead_summary");
      }
    } catch {
      // ignore
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#ffffff" }}>
      {/* Navbar */}
      <nav
        className="px-6 py-4 flex items-center justify-between"
        style={{ borderBottom: "1px solid rgba(46,0,79,0.1)", backgroundColor: "#ffffff" }}
      >
        <Link href="/">
          <span className="flex items-center cursor-pointer">
            <img src={LOGO_ORANGE} alt="RideCloud" className="h-9 w-auto object-contain" />
          </span>
        </Link>
        <Link href="/">
          <span
            className="flex items-center gap-2 text-sm cursor-pointer transition-colors"
            style={{ color: "#5a3d70" }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to website
          </span>
        </Link>
      </nav>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-16" style={{ backgroundColor: "#f8f7fa" }}>
        <div className="w-full max-w-2xl">

          {/* Confirmation card */}
          <div
            className="rounded-lg p-8 md:p-10 text-center mb-6"
            style={{ backgroundColor: "#ffffff", border: "1px solid rgba(46,0,79,0.12)" }}
          >
            {/* Checkmark */}
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: "rgba(230,83,30,0.1)", border: "2px solid rgba(230,83,30,0.3)" }}
            >
              <CheckCircle className="w-8 h-8" style={{ color: "#e6531e" }} />
            </div>

            <div
              className="inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-4"
              style={{ backgroundColor: "rgba(230,83,30,0.08)", color: "#e6531e", border: "1px solid rgba(230,83,30,0.2)" }}
            >
              Enquiry Received
            </div>

            <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: "#2e004f" }}>
              {lead?.name
                ? <>Thank you, <span style={{ color: "#e6531e" }}>{lead.name.split(" ")[0]}!</span></>
                : <>Thank You for <span style={{ color: "#e6531e" }}>Reaching Out!</span></>
              }
            </h1>

            <p className="text-base leading-relaxed mb-8 max-w-md mx-auto" style={{ color: "#5a3d70" }}>
              Your enquiry has been logged and a RideCloud consultant will be in touch within{" "}
              <strong style={{ color: "#2e004f" }}>24 business hours</strong>.
            </p>

            {/* Quick contact strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {[
                { icon: Phone, label: "Call Us", value: "+27 83 656 5677", href: "tel:+27836565677" },
                { icon: Mail, label: "Email Us", value: "sales@ridecloud.co.za", href: "mailto:sales@ridecloud.co.za" },
                { icon: Clock, label: "Office Hours", value: "Mon–Fri 08:00–17:00", href: null },
              ].map((item) => {
                const Icon = item.icon;
                const inner = (
                  <div
                    className="flex flex-col items-center gap-1.5 p-4 rounded-lg h-full"
                    style={{ backgroundColor: "#f8f7fa", border: "1px solid rgba(46,0,79,0.08)" }}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "rgba(46,0,79,0.08)" }}
                    >
                      <Icon className="w-4 h-4" style={{ color: "#2e004f" }} />
                    </div>
                    <div className="text-xs" style={{ color: "#9a8aaa" }}>{item.label}</div>
                    <div className="text-xs font-semibold text-center" style={{ color: "#2e004f" }}>{item.value}</div>
                  </div>
                );
                return item.href ? (
                  <a key={item.label} href={item.href} className="block hover:opacity-80 transition-opacity">{inner}</a>
                ) : (
                  <div key={item.label}>{inner}</div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/#vehicles">
                <span className="btn-orange cursor-pointer">Browse More Vehicles</span>
              </Link>
              <Link href="/">
                <span
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold cursor-pointer transition-all"
                  style={{ border: "2px solid #2e004f", color: "#2e004f", backgroundColor: "transparent" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#2e004f"; e.currentTarget.style.color = "#ffffff"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#2e004f"; }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </span>
              </Link>
            </div>
          </div>

          {/* Submission summary card */}
          {lead && (
            <div
              className="rounded-lg p-6 mb-6"
              style={{ backgroundColor: "#ffffff", border: "1px solid rgba(46,0,79,0.12)" }}
            >
              <div className="flex items-center gap-2 mb-5">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "rgba(230,83,30,0.1)" }}
                >
                  <CheckCircle className="w-4 h-4" style={{ color: "#e6531e" }} />
                </div>
                <div>
                  <h2 className="text-base font-bold" style={{ color: "#2e004f" }}>Your Submission Summary</h2>
                  <p className="text-xs" style={{ color: "#9a8aaa" }}>
                    {lead.source === "vehicle-enquiry" ? "Submitted via Vehicle Enquiry" : "Submitted via Contact Form"}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {lead.name && (
                  <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: "#f8f7fa" }}>
                    <User className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#e6531e" }} />
                    <div>
                      <div className="text-xs font-semibold mb-0.5" style={{ color: "#9a8aaa" }}>Full Name</div>
                      <div className="text-sm font-medium" style={{ color: "#2e004f" }}>{lead.name}</div>
                    </div>
                  </div>
                )}
                {lead.vehicle && (
                  <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: "rgba(230,83,30,0.04)", border: "1px solid rgba(230,83,30,0.15)" }}>
                    <Car className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#e6531e" }} />
                    <div>
                      <div className="text-xs font-semibold mb-0.5" style={{ color: "#9a8aaa" }}>Vehicle of Interest</div>
                      <div className="text-sm font-bold" style={{ color: "#e6531e" }}>{lead.vehicle}</div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {lead.phone && (
                    <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: "#f8f7fa" }}>
                      <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#2e004f" }} />
                      <div>
                        <div className="text-xs font-semibold mb-0.5" style={{ color: "#9a8aaa" }}>Phone</div>
                        <div className="text-sm font-medium" style={{ color: "#2e004f" }}>{lead.phone}</div>
                      </div>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: "#f8f7fa" }}>
                      <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#2e004f" }} />
                      <div>
                        <div className="text-xs font-semibold mb-0.5" style={{ color: "#9a8aaa" }}>Email</div>
                        <div className="text-sm font-medium" style={{ color: "#2e004f" }}>{lead.email}</div>
                      </div>
                    </div>
                  )}
                </div>
                {lead.comments && (
                  <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: "#f8f7fa" }}>
                    <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#2e004f" }} />
                    <div>
                      <div className="text-xs font-semibold mb-0.5" style={{ color: "#9a8aaa" }}>Comments</div>
                      <div className="text-sm" style={{ color: "#2e004f" }}>{lead.comments}</div>
                    </div>
                  </div>
                )}
                {lead.submittedAt && (
                  <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: "#f8f7fa" }}>
                    <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#2e004f" }} />
                    <div>
                      <div className="text-xs font-semibold mb-0.5" style={{ color: "#9a8aaa" }}>Submitted</div>
                      <div className="text-sm" style={{ color: "#2e004f" }}>{formatDate(lead.submittedAt)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* What happens next */}
          <div
            className="rounded-lg p-6"
            style={{ backgroundColor: "#ffffff", border: "1px solid rgba(46,0,79,0.12)" }}
          >
            <h2 className="text-lg font-bold mb-5" style={{ color: "#2e004f" }}>What Happens Next?</h2>
            <div className="space-y-4">
              {steps.map((step) => (
                <div key={step.number} className="flex items-start gap-4">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                    style={{ backgroundColor: "#e6531e", color: "#ffffff" }}
                  >
                    {step.number}
                  </div>
                  <div>
                    <div className="text-sm font-bold mb-0.5" style={{ color: "#2e004f" }}>{step.title}</div>
                    <div className="text-sm leading-relaxed" style={{ color: "#5a3d70" }}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </main>

      {/* Footer strip */}
      <div
        className="py-4 text-center text-xs"
        style={{ backgroundColor: "#2e004f", color: "rgba(255,255,255,0.5)" }}
      >
        © 2026 RideCloud. All rights reserved. Registered in South Africa.
      </div>
    </div>
  );
}
